import angular from 'angular';

(() => {
  class QuestionnaireService {
    /**
     * Interface for our questionnaires
     * @memberof pims
     * @ngdoc service
     * @param {service} $http xhr library
     * @param {string} API_URL our base api uri
     */
    constructor($http, Utils, API_URL) {
      'ngInject';

      Object.assign(this, {
        $http,
        Utils,
        API_URL,
      });
    }
    /**
     * Clone an existing auestionnaire
     * @param {number} questionnaireId
     */

    clone(questionnaireId) {
      return this.fetch(questionnaireId).then(({ data: q }) => this.create(q));
    }
    /**
     * Clone an existing auestionnaire to another service / category
     * @param {number} questionnaireId
     */

    clonToOtherService(questionnaireId, category_code) {
      return this.fetch(questionnaireId).then(({ data: q }) => {
        q.category_code = category_code; // return this.$http.post(`${this.API_URL}/admin/questionnaires`, q);

        return this.create(q);
      });
    }
    /**
     * Create a new questionnaire
     * @param {object} questionnaire
     */

    create(questionnaire) {
      return this.$http.post(`${this.API_URL}/admin/questionnaires`, questionnaire);
    }
    /**
     * Delete a questionnaire
     * @param {number} questionnaireId
     */

    delete(questionnaireId) {
      return this.$http.delete(`${this.API_URL}/admin/questionnaires/${questionnaireId}`);
    }
    getMapping(id) {
      return this.$http.get(`${this.API_URL}/admin/questionnaires/questions/${id}/answermappings/?cache=0`);
    }

    /**
     * Retrieve a questionnaire
     * @param {number} questionnaireId
     */

    fetch(questionnaireId) {
      return this.$http.get(`${this.API_URL}/admin/questionnaires/${questionnaireId}`).then(data => {
        // initialise default params when undefined
        data.data.sections.forEach(section => {
          section.questions.forEach(question => {
            if (question.type === 'select_combo') {
              this.$http
                .get(`${this.API_URL}/admin/questionnaires/questions/${question.id}/answermappings/?cache=0`)
                .then(res => {
                  question.additional_fields = res.data.data;
                });
            }
            question.answers.forEach(answer => {
              if (answer.details && !answer.type) {
                answer.type = 'text';
              }
            });
          });
        });
        return data;
      });
    }
    /**
     * Get all questionnaires associated with a category
     * @param {string} serviceCode - service to pull related questionnaires
     */

    list(serviceCode) {
      return this.$http.get(`${this.API_URL}/admin/questionnaires?category_code=${serviceCode}`);
    }
    /**
     * Update existing questionnaire
     * @param {number} questionnaireId - id
     * @param {object} changes - new questionnaire fields
     */

    update(questionnaireId, changes = {}) {
      const whitelist = ['name', 'description', 'phase'];
      const filtered = this.Utils.filterParams(changes, whitelist);
      return this.$http.put(`${this.API_URL}/admin/questionnaires/${questionnaireId}`, filtered);
    }
    /* ========================================================
      SECTIONS ARE TOP MOST ELEMENT IN QUESTIONNAIRE
    ======================================================== */

    /**
     * Adds a section to a questionnaire
     * @param {number} questionnaireId
     * @param {object} section
     */

    addSection(questionnaireId, section) {
      return this.$http.post(`${this.API_URL}/admin/questionnaires/${questionnaireId}/sections`, section, {
        loadingText: 'Creating Section',
      });
    }
    /**
     * Deletes an existing section
     * @param {number} sectionId
     */

    updateSection(section) {
      return this.$http.put(`${this.API_URL}/admin/questionnaires/sections/${section.id}`, section, {
        loadingText: 'Updating Section',
      });
    }
    /**
     * Deletes an existing section
     * @param {number} sectionId
     */

    deleteSection(sectionId) {
      return this.$http.delete(`${this.API_URL}/admin/questionnaires/sections/${sectionId}`, {
        loadingText: 'Removing Section',
      });
    }
    /**
     * Updates the positions of sections within a questionnaire
     * @param {number} questionnaireId
     * @param {array} sectionIds - new ordering of sections
     */

    updateSectionPositions(questionnaireId, sectionIds) {
      return this.$http.put(`${this.API_URL}/admin/questionnaires/${questionnaireId}/sections/positions`, sectionIds, {
        loadingText: 'Updating Section Order',
      });
    }
    /* ========================================================
      QUESTIONS BELONG TO SECTIONS
    ======================================================== */

    /**
     * Adds a question to a section
     * @param {number} sectionId
     * @param {object} question
     */

    addQuestion(sectionId, question) {
      if (!question.hasOwnProperty('required')) {
        question.required = false;
      }

      return this.$http.post(`${this.API_URL}/admin/questionnaires/sections/${sectionId}/questions`, question, {
        loadingText: 'Adding Question',
      });
    }
    /**
     * Updates an existing question
     * @param {number} questionId
     * @param {object} question
     */

    updateQuestion(questionId, question) {
      return this.$http.put(`${this.API_URL}/admin/questionnaires/questions/${questionId}`, question, {
        loadingText: 'Updating Question',
      });
    }
    /**
     * Deletes an existing question
     * @param {number} questionId
     */

    deleteQuestion(questionId) {
      return this.$http.delete(`${this.API_URL}/admin/questionnaires/questions/${questionId}`, {
        loadingText: 'Deleting Question',
      });
    }
    /**
     * Updtes question order
     * @param {number} sectionId - id of section questions belong to
     * @param {array} questionIds - new order of questions
     */

    updateQuestionPositions(sectionId, questionIds) {
      return this.$http.put(
        `${this.API_URL}/admin/questionnaires/sections/${sectionId}/questions/positions`,
        questionIds,
        {
          loadingText: 'Updating Question Order',
        },
      );
    }
    /* ========================================================
      ANSWERS BELONG TO QUESTIONS
    ======================================================== */

    /**
     * Adds an answer to a question
     * @param {number} questionId
     * @param {object} answer
     */

    addAnswer(questionId, answer) {
      return this.$http.post(`${this.API_URL}/admin/questionnaires/questions/${questionId}/answers`, answer);
    }
    /**
     * Updates an existing answer
     * @param {number} answerId
     * @param {object} answer
     */

    updateAnswer(answerId, answer) {
      return this.$http.put(`${this.API_URL}/admin/questionnaires/answers/${answerId}`, answer);
    }
    /**
     * Deletes an existing answer
     * @param {number} answerId
     */

    deleteAnswer(answerId) {
      return this.$http.delete(`${this.API_URL}/admin/questionnaires/answers/${answerId}`);
    }

    deleteAnswerNew(answerId, isCombinationMappings) {
      if (isCombinationMappings) {
        return this.$http.delete(`${this.API_URL}/admin/answermappings/${answerId}`);
      } else {
        return this.$http.delete(`${this.API_URL}/admin/questionnaires/answers/${answerId}`);
      }
    }

    /**
     * Updates answer order
     * @param {number} question - id of question answers belong to
     * @param {array} answerIds - new order of answers
     */
    updateAnswerPositions(questionId, answerIds) {
      return this.$http.put(
        `${this.API_URL}/admin/questionnaires/questions/${questionId}/answers/positions`,
        answerIds,
        { loadingText: 'Updating Answer Order' },
      );
    }

    postAnswerMappings(expression) {
      return this.$http.post(`${this.API_URL}/admin/answermappings/`, expression, {
        loadingText: 'Adding Combination',
      });
    }

    /**
     * Updates answermappings / combination for batching
     * @param {number} answerMappingId - id of the answermappings / combination for batching
     * @param {object} newAnswerMapping - new answermappings / combination for batching
     */
    updateAnswerMappings(answerMappingId, newAnswerMapping) {
      return this.$http.put(`${this.API_URL}/admin/answermappings/${answerMappingId}`, newAnswerMapping, {
        loadingText: 'Adding Combination',
      });
    }

    cloneQuestionnaire(questionnaireId) {
      return this.$http.post(`${this.API_URL}/admin/questionnaires/${questionnaireId}/clone`);
    }

    cloneToOtherService(questionnaireId, categoryCode) {
      return this.$http.post(`${this.API_URL}/admin/questionnaires/${questionnaireId}/clone/${categoryCode}`);
    }
  }

  QuestionnaireService.$inject = ['$http', 'Utils', 'API_URL'];
  angular.module('pims').service('QuestionnaireService', QuestionnaireService);
})();
